<script lang="ts">
import { mapActions, mapGetters, mapState } from 'vuex';
import {
  iBadge,
  iBadgeColours,
  iButton,
  iButtonSizes,
  iButtonStyles,
  iButtonTypes,
  iButtonsGroup,
  iSearchInput,
  iMenu,
  iMenuItem,
  iMenuTriggerAlignOptions,
  iModal,
  iPagination,
  iTable,
  iTableRow,
  iTableSortButton,
  openModal,
  showToast,
} from '@inscribe/signal';
import * as userRoles from '@/constants/userRoles';
import IconText from '@/V2/components/IconText.vue';
import SettingsSectionTitle from '@/V2/settings/shared/SettingsSectionTitle';
import TeamSettingsUserEdit from '@/V2/settings/TeamSettingsUserEdit';
import palette from '@inscribe/signal/dist/palette';
import EmptyPage from '@/shared/components/EmptyPage';
import FilterButton, {
  LevelFilter,
} from '@/V2/components/filters/FilterButton.vue';

const USER_STATUSES = {
  ENABLED: 'enabled',
  DISABLED: 'disabled',
  INVITED: 'invited',
  ALL: 'all',
};

export default {
  components: {
    EmptyPage,
    FilterButton,
    iBadge,
    iButton,
    iButtonsGroup,
    IconText,
    iMenu,
    iMenuItem,
    iModal,
    iPagination,
    iSearchInput,
    iTable,
    iTableRow,
    iTableSortButton,
    SettingsSectionTitle,
    TeamSettingsUserEdit,
  },
  data() {
    return {
      iBadgeColours,
      iButtonSizes,
      iButtonStyles,
      iButtonTypes,
      iMenuTriggerAlignOptions,
      loadingIds: {},
      palette,
      sortType: 'user',
      sortOrder: 'asc',
      confirmModalLastAction: '',
      confirmModalTitle: '',
      confirmModalText: '',
      confirmModalButtonLabel: '',
      confirmModalType: '',
      currentEditingUser: null,
      filterState: USER_STATUSES.ENABLED,
      perPage: 10,
      currentPage: 1,
      USER_STATUSES,
      usersLoading: false,
      allUsers: [],
      allRoles: [
        {
          value: userRoles.ADMIN_ROLE,
          text: 'Admin',
          subtext:
            'Can manage all customers, documents, and users in this workspace',
        },
        {
          value: userRoles.ADVANCED_USER_ROLE,
          text: 'Advanced User',
          subtext: 'Can manage all customers and documents in this workspace',
        },
        {
          value: userRoles.ADVANCED_READONLY_USER_ROLE,
          text: 'Advanced User (Read Only)',
          subtext: 'Can view all customers and documents in this workspace',
        },
        {
          value: userRoles.USER_ROLE,
          text: 'User',
          subtext: 'Can manage their own customers and documents',
        },
        {
          value: userRoles.BASIC_USER_ROLE,
          text: 'Basic User',
          subtext: 'Can view their own customers and documents',
        },
        {
          value: userRoles.BASIC_READONLY_USER_ROLE,
          text: 'Basic User (Read Only)',
          subtext:
            'Can only view customers for which they have received an access grant (via the API)',
        },
      ],
      searchQuery: '',
    };
  },
  computed: {
    ...mapGetters({
      hasEditPermissions: 'isOrgAdmin',
      canAccessUserManagementApi: 'canAccessUserManagementApi',
    }),

    ...mapState(['currentUser']),

    userFilters(): LevelFilter[] {
      const filterCommonConfig = {
        icon: 'small_solid_user_1',
        queryParam: '',
      };
      return [
        {
          ...filterCommonConfig,
          id: USER_STATUSES.ENABLED,
          title: `${this.countEnabled}`,
          subtitle: 'Enabled',
        },
        {
          ...filterCommonConfig,
          id: USER_STATUSES.DISABLED,
          title: `${this.countDisabled}`,
          subtitle: 'Disabled',
        },
        {
          ...filterCommonConfig,
          id: USER_STATUSES.INVITED,
          title: `${this.countInvited}`,
          subtitle: 'Invited',
        },
        {
          ...filterCommonConfig,
          id: USER_STATUSES.ALL,
          title: `${this.countTotal}`,
          subtitle: 'All',
        },
      ];
    },

    roles() {
      if (
        this.canAccessUserManagementApi ||
        this.currentEditingUser?.role === userRoles.BASIC_READONLY_USER_ROLE
        // Edge case: FF off but basic readonly user exists from when FF was on previously
        // The current role of the user being edited must still be shown in this case.
      ) {
        return this.allRoles;
      }

      return this.allRoles.filter(
        (role) => role.value !== userRoles.BASIC_READONLY_USER_ROLE,
        // FF is off => can't invite user of this role or change a user's role to it.
      );
    },
    usersList() {
      const users = this.filteredUsers;
      // sort
      const orderValue = this.sortOrder === 'asc' ? -1 : 1;
      switch (this.sortType) {
        case 'user':
          users.sort((u1, u2) => {
            return u1.email > u2.email ? -1 * orderValue : 1 * orderValue;
          });
          break;
        case 'role':
          users.sort((u1, u2) => {
            return this.getRoleConfig(u1.role).text >
              this.getRoleConfig(u2.role).text
              ? -1 * orderValue
              : 1 * orderValue;
          });
          break;
        case 'state':
          users.sort((u1, u2) => {
            return this.getUserConfig(u1).label > this.getUserConfig(u2).label
              ? -1 * orderValue
              : 1 * orderValue;
          });
          break;
      }
      const paginationStart = this.perPage * (this.currentPage - 1);
      return users.slice(paginationStart, paginationStart + this.perPage);
    },
    countTotal() {
      return this.allUsers.length;
    },
    countEnabled() {
      return this.allUsers.filter(
        (user) => user.is_active && user.registration_confirmed,
      ).length;
    },
    countDisabled() {
      return this.allUsers.filter(
        (user) => !user.is_active && user.registration_confirmed,
      ).length;
    },
    countInvited() {
      return this.allUsers.filter((user) => !user.registration_confirmed)
        .length;
    },

    filteredUsers() {
      return this.allUsers
        .filter(
          (user) =>
            user.email.toLowerCase().includes(this.searchQuery.toLowerCase()) ||
            user.name.toLowerCase().includes(this.searchQuery.toLowerCase()),
        )
        .filter((user) => {
          switch (this.filterState) {
            case USER_STATUSES.ENABLED:
              return user.is_active && user.registration_confirmed;
            case USER_STATUSES.DISABLED:
              return !user.is_active && user.registration_confirmed;
            case USER_STATUSES.INVITED:
              return !user.registration_confirmed;
            default:
              return true;
          }
        });
    },

    showPagination() {
      return this.filteredUsers.length > this.perPage;
    },
  },
  mounted() {
    this.loadUsers();
    if (this.$route.query?.showInviteModal?.toString() === 'true') {
      this.startInviteUser();
    }
  },
  methods: {
    ...mapActions([
      'getUsers',
      'cancelUserInvite',
      'reinviteUser',
      'changeUserActiveStatus',
    ]),
    getUserChangeStatusColor(user) {
      return user.is_active
        ? palette['light-error-90']
        : palette['light-neutral-120'];
    },
    loadUsers() {
      this.usersLoading = true;
      this.getUsers()
        .then((users) => {
          this.usersLoading = false;
          this.allUsers = users.data?.data;
        })
        .finally(() => {
          this.usersLoading = false;
        });
    },
    onSearchQueryChanged(query) {
      this.searchQuery = query;
    },
    openConfirmModal(user, action) {
      this.confirmModalLastAction = action;
      this.confirmModalLastUser = user;
      this.confirmModalType = 'primary';
      switch (action) {
        case 'change-status':
          this.confirmModalTitle = user.is_active
            ? 'Disable User'
            : 'Enable User';
          this.confirmModalButtonLabel = user.is_active ? 'Disable' : 'Enable';
          this.confirmModalText = user.is_active
            ? 'Are you sure you want to disable this user?'
            : 'Would you like to enable this user?';
          this.confirmModalType = user.is_active ? 'error' : 'primary';
          break;
        case 'resend-invite':
          this.confirmModalTitle = 'Resend Invite';
          this.confirmModalButtonLabel = 'Resend';
          this.confirmModalText =
            'Would you like to send this invitation again?';
          break;
        case 'cancel-invite':
          this.confirmModalTitle = 'Cancel Invite';
          this.confirmModalButtonLabel = 'Cancel Invite';
          this.confirmModalText = 'Would you like to cancel this invitation?';
          this.confirmModalType = 'error';
          break;
      }
      openModal('confirm-modal');
    },
    onConfirmSubmit() {
      switch (this.confirmModalLastAction) {
        case 'change-status':
          this.tryChangeUserActiveStatus(this.confirmModalLastUser);
          break;
        case 'resend-invite':
          this.tryReinviteUser(this.confirmModalLastUser?.id);
          break;
        case 'cancel-invite':
          this.tryCancelUserInvite(this.confirmModalLastUser?.id);
          break;
      }
    },
    onConfirmClose() {
      this.confirmModalLastAction = null;
      this.confirmModalLastUser = null;
    },
    onRowClick(user) {
      if (this.hasEditPermissions) {
        this.currentEditingUser = user;
      }
    },
    getRoleConfig(role) {
      return this.allRoles.find((currRole) => role === currRole.value);
    },
    getUserConfig(user) {
      const config = {
        role: this.getRoleConfig(user.role),
        label: 'Enabled',
        color: iBadgeColours.SUCCESS,
      };
      if (!user.registration_confirmed && user.registration_invite_expired) {
        config.label = 'Expired';
        config.color = iBadgeColours.ERROR;
      } else if (!user.registration_confirmed) {
        config.label = 'Pending';
        config.color = iBadgeColours.WARNING;
      } else if (!user.is_active) {
        config.label = 'Disabled';
        config.color = iBadgeColours.NEUTRAL;
      }
      return config;
    },
    onOrderChange(field, order) {
      this.sortType = field;
      this.sortOrder = order;
    },
    tryChangeUserActiveStatus(user) {
      const userId = user.id;
      const activeStatus = !user.is_active;
      this.$set(this.loadingIds, userId, true);
      this.changeUserActiveStatus({ userId, activeStatus })
        .then((response) => {
          if (response.data.success) {
            this.loadUsers();
            showToast({
              text: 'User status changed correctly',
            });
          }
        })
        .catch((error) => {
          const statusChange = activeStatus ? 'disabling' : 're-enabling';
          showToast({
            title: `There was a problem ${statusChange} the user`,
            text: error?.response?.data?.message,
            isError: true,
          });
        })
        .finally(() => {
          this.$set(this.loadingIds, user.id, false);
        });
    },
    tryReinviteUser(userId) {
      this.$set(this.loadingIds, userId, true);
      this.reinviteUser(userId)
        .then(() => {
          this.$set(this.loadingIds, userId, false);
          const foundUser = this.allUsers.find((user) => user.id === userId);
          if (foundUser) {
            foundUser.registration_invite_expired = false;
          }
          showToast({
            text: 'Invite resent',
          });
          this.loadUsers();
        })
        .catch((error) => {
          this.$set(this.loadingIds, userId, false);
          showToast({
            title: 'There was a problem resending your invite',
            text: error?.response?.data?.message,
            isError: true,
          });
        });
    },
    tryCancelUserInvite(userId) {
      this.$set(this.loadingIds, userId, true);
      this.cancelUserInvite(userId)
        .then((response) => {
          if (response.data.success) {
            showToast({
              text: 'Invite cancelled',
            });
            this.loadUsers();
          }
        })
        .catch((error) => {
          showToast({
            title: 'There was a problem cancelling the invite',
            text: error?.response?.data?.message,
            isError: true,
          });
        })
        .finally(() => {
          this.$set(this.loadingIds, userId, false);
        });
    },
    startInviteUser() {
      this.currentEditingUser = {};
    },
    getUserName(user) {
      return user.name && user.name !== '' ? user.name : user.email;
    },
    setFilterStatus(filter: LevelFilter) {
      this.filterState = filter.id;
    },
    onUserEdited() {
      this.currentEditingUser = null;
      this.loadUsers();
    },
  },
};
</script>
<template>
  <div class="team-settings">
    <div class="justify-content-center">
      <div class="d-flex flex-column i-gap-12 i-px-0">
        <!-- title -->
        <div class="d-flex justify-content-between align-items-center">
          <div>
            <settings-section-title id="your-team" class="i-mb-2"
              >Team</settings-section-title
            >
            <p class="i-text-body-2 i-text-neutral-110">
              View and manage how your team is using Inscribe
            </p>
          </div>
          <i-button
            v-if="hasEditPermissions"
            text="Invite User"
            size="md"
            @button-click="startInviteUser"
          >
          </i-button>
        </div>

        <!-- search input -->
        <i-search-input
          input-placeholder="Search users"
          :debounce-time="200"
          :filled-value="searchQuery"
          data-dd-action-name="team-settings-search-input"
          @input="onSearchQueryChanged"
        />

        <!-- filters -->
        <div class="filters-container d-flex flex-row i-gap-3">
          <filter-button
            v-for="filter in userFilters"
            :key="filter.id"
            :filter="filter"
            :selected="filterState === filter.id"
            class="button-filter"
            @filter-click="setFilterStatus(filter)"
            :data-dd-action-name="`team-settings-user-status-filter-button-${filter.id}`"
          />
        </div>

        <!-- table container -->
        <i-table
          v-if="usersList.length"
          :loading="usersLoading"
          class="users-table"
        >
          <!-- table header -->
          <template #header>
            <i-table-row type="header">
              <template #cells>
                <div class="no-padding-x">
                  <i-table-sort-button
                    label="User"
                    @order-change="(order) => onOrderChange('user', order)"
                  ></i-table-sort-button>
                </div>
                <div>
                  <i-table-sort-button
                    label="Role"
                    @order-change="(order) => onOrderChange('role', order)"
                  ></i-table-sort-button>
                </div>
                <div>
                  <i-table-sort-button
                    label="Status"
                    @order-change="(order) => onOrderChange('state', order)"
                  ></i-table-sort-button>
                </div>
              </template>
            </i-table-row>
          </template>

          <!-- table body -->
          <template #rows>
            <i-table-row
              v-for="user of usersList"
              :key="user.id"
              class="selectable"
              @on-row-click="onRowClick(user)"
            >
              <template #cells>
                <!-- user data -->
                <div class="no-padding-x">
                  <icon-text
                    data-dd-action-name="team-settings-user-name-link"
                    icon="medium-user-single"
                    :title="getUserName(user)"
                    :subtitle="user.email"
                    class="i-py-4"
                  ></icon-text>
                </div>

                <!-- role-->
                <div class="role centered">
                  <i-badge
                    :colour="iBadgeColours.NEUTRAL"
                    :label="getUserConfig(user).role?.text"
                    :outline="true"
                  ></i-badge>
                </div>

                <!-- state-->
                <div class="state centered">
                  <i-badge
                    :colour="getUserConfig(user).color"
                    :label="getUserConfig(user).label"
                  ></i-badge>
                </div>

                <!-- menu -->
                <div class="menu-container centered">
                  <i-menu
                    v-if="hasEditPermissions"
                    ref="menuEl"
                    :trigger-align="iMenuTriggerAlignOptions.RIGHT"
                    :width="180"
                    class="d-flex align-items-center"
                  >
                    <template #trigger>
                      <i-button
                        class="trigger-button"
                        :left-icon="'medium-more-menu-vertical'"
                        :type="iButtonTypes.NEUTRAL"
                        :button-style="iButtonStyles.GHOST"
                      />
                    </template>

                    <template #items>
                      <!-- change User Status -->
                      <i-menu-item
                        v-if="user.registration_confirmed"
                        :label="user.is_active ? 'Disable' : 'Enable' + ' user'"
                        :icon-color="getUserChangeStatusColor(user)"
                        :text-color="getUserChangeStatusColor(user)"
                        :icon="
                          user.is_active
                            ? 'small-user-reject'
                            : 'small-user-approve'
                        "
                        @item-click="openConfirmModal(user, 'change-status')"
                      ></i-menu-item>

                      <!-- resend invite -->
                      <i-menu-item
                        v-if="
                          !user.registration_confirmed && hasEditPermissions
                        "
                        :disabled="loadingIds[user.id]"
                        label="Resend invite"
                        icon="small-mail-refresh"
                        @item-click="openConfirmModal(user, 'resend-invite')"
                      ></i-menu-item>

                      <!-- cancel user invite -->
                      <i-menu-item
                        v-if="
                          !user.registration_confirmed &&
                          !user.registration_invite_expired &&
                          hasEditPermissions
                        "
                        :icon-color="palette['light-error-90']"
                        :text-color="palette['light-error-90']"
                        :disabled="loadingIds[user.id]"
                        label="Cancel invite"
                        icon="small-mail-remove"
                        @item-click="openConfirmModal(user, 'cancel-invite')"
                      ></i-menu-item>
                    </template>
                  </i-menu>
                </div>
              </template>
            </i-table-row>
          </template>
        </i-table>

        <empty-page
          v-else
          icon="large_multiple_users_search_1"
          title="No users found"
          message="Invite your team to join Inscribe"
        ></empty-page>

        <!-- pagination-->
        <i-pagination
          v-if="showPagination"
          size="md"
          :total-items="filteredUsers.length"
          :items-per-page="perPage"
          :selected-page="currentPage"
          @page-click="(page) => (currentPage = page)"
        ></i-pagination>
      </div>
    </div>

    <!-- confirm modal -->
    <i-modal
      modal-id="confirm-modal"
      :title="confirmModalTitle"
      :confirm-button-text="confirmModalButtonLabel"
      :type="confirmModalType"
      @on-submit="onConfirmSubmit"
      @on-close="onConfirmClose"
    >
      <template #body-content>
        <div>
          {{ confirmModalText }}
        </div>
      </template>
    </i-modal>

    <!-- create/edit user-->
    <team-settings-user-edit
      :user="currentEditingUser"
      :roles="roles"
      @hide-drawer="currentEditingUser = null"
      @users-edited="onUserEdited"
    ></team-settings-user-edit>

    <router-view />
  </div>
</template>

<style lang="scss" scoped>
.team-settings {
  .button-filter {
    width: 100%;
  }

  .users-table {
    .user-icon {
      min-width: 40px;
    }

    .i-badge {
      width: max-content;
    }

    .menu-container {
      width: 40px;
    }
  }
}
</style>
